import moment from 'moment';
import { extractName } from 'flow/utils';
import { getApplicationLanguage } from 'flow/utils/browser-utils';

export const DEFAULT_SORTING_LANGUAGE = 'sv';

const comparisonValueIfFalsyArguments = (obj1, obj2) => {
    if (!obj1 && !obj2) {
        return 0;
    } else if (!obj1) {
        return 1;
    } else if (!obj2) {
        return -1;
    }
    return undefined;
};

/* localeCompare with a language builds a collator on every call, which is
 * most of the cost when a list of thousands sorts. One collator serves until
 * the language changes. */
let collator;
let collatorLanguage;

const sortByUserLanguage = (string1, string2) => {
    const language =
        getApplicationLanguage()?.toLowerCase() || DEFAULT_SORTING_LANGUAGE;
    if (language !== collatorLanguage) {
        collator = new Intl.Collator(language);
        collatorLanguage = language;
    }
    return collator.compare(string1, string2);
};

export const sortStrings = (string1, string2) => {
    const comparisonValue = comparisonValueIfFalsyArguments(string1, string2);

    if (comparisonValue !== undefined) {
        return comparisonValue;
    }
    return sortByUserLanguage(string1, string2);
};

export const contactNameSorter = (contact0, contact1) => {
    const name0 = extractName(contact0, '');
    const name1 = extractName(contact1, '');

    return sortStrings(name0, name1);
};

/**
 * Comparator for sorting a list of dates
 * descendingly, meaning newer date-times first.
 *
 * This comparator makes falsy values end up last
 * in lists.
 *
 * @param dateTimeString1 dateTimeString1 'yyyy-mm-ddTHH:mm[.SSS+ZZZ]'
 * @param dateTimeString2 dateTimeString2 'yyyy-mm-ddTHH:mm[.SSS+ZZZ]'
 * @returns {Number}        one of 1, -1, or 0
 */
export const newestFirstSorter = (dateTimeString1, dateTimeString2) => {
    return dateSorter(dateTimeString1, dateTimeString2, true);
};

/**
 * Comparator for sorting a list of dates
 * ascendingly, meaning older date-times first.
 *
 * This comparator makes falsy values end up last
 * in lists.
 *
 * @param dateTimeString1 dateTimeString1 'yyyy-mm-ddTHH:mm[.SSS+ZZZ]'
 * @param dateTimeString2 dateTimeString2 'yyyy-mm-ddTHH:mm[.SSS+ZZZ]'
 * @returns {Number}        one of 1, -1, or 0
 */
export const oldestFirstSorter = (dateTimeString1, dateTimeString2) => {
    return dateSorter(dateTimeString1, dateTimeString2, false);
};

const dateSorter = (dateTimeString1, dateTimeString2, newestFirst) => {
    const comparisonValue = comparisonValueIfFalsyArguments(
        dateTimeString1,
        dateTimeString2,
    );

    if (comparisonValue !== undefined) {
        return comparisonValue;
    }

    const dateTime1 = moment(dateTimeString1);
    const dateTime2 = moment(dateTimeString2);

    // We want to sort list descendingly IF newestFirst = true (otherwise ascendingly).
    // If the first date-time is before the second date-time,
    // it is older and we want to change the order of them.
    // This means returning 1 from this function.
    if (dateTime1.isBefore(dateTime2)) {
        return newestFirst ? 1 : -1;
    } else if (dateTime2.isBefore(dateTime1)) {
        return newestFirst ? -1 : 1;
    }
    return 0;
};
